/**
 * P2-10: CraftTable —— 合成表（迁移自 WCraftTable，js/jsx/left.jsx.js:677-755）。
 *
 * - 整表前提：game.bld.get("workshop").on <= 0 → 返回 null（left.jsx.js:700-702）；
 * - 只迭代 resPool.resources 中 res.craftable 项（left.jsx.js:689-693；伪资源不进合成表）；
 * - 折叠/编辑为组件 state，不持久化（left.jsx.js:681-686，与旧版一致）；
 * - 工具条结构与旧版一致：左折叠链接（title "Toggle craft"，折叠文本 ">(left.craft)"）
 *   + 右 gear（无 title、无问号 tooltip——与主资源表不同，left.jsx.js:704-734）；
 * - 外壳 class `sidebar-section` 保留（主题钩子）。
 */
import { useCallback, useState } from "react";
import type { KeyboardEvent } from "react";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";
import { CraftRow } from "./CraftRow";
import type { ResLike } from "./ResourceRow";

// ---------------------------------------------------------------------------
// 表级快照：craftable 资源名序列 + workshop.on 前提 + reqRes
// ---------------------------------------------------------------------------

interface CraftTableSnapshot {
  workshopOn: boolean;
  names: string[];
  reqRes: string[];
}

function stringArrayEq(a: string[], b: string[]): boolean {
  if (a === b) {
    return true;
  }
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

function tableSnapshotEq(a: CraftTableSnapshot | null, b: CraftTableSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.workshopOn === b.workshopOn &&
    stringArrayEq(a.names, b.names) &&
    stringArrayEq(a.reqRes, b.reqRes)
  );
}

export function computeCraftTableSnapshot(game: GamePage | null): CraftTableSnapshot | null {
  if (!game) {
    return null;
  }
  const names: string[] = [];
  for (const res of game.resPool.resources as ResLike[]) {
    if (res.craftable) {
      names.push(res.name);
    }
  }
  return {
    workshopOn: game.bld.get("workshop").on > 0,
    names,
    reqRes: game.getRequiredResources(game.selectedBuilding) as string[],
  };
}

// ---------------------------------------------------------------------------
// CraftTable
// ---------------------------------------------------------------------------

export function CraftTable() {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const selector = useCallback((game: GamePage | null) => computeCraftTableSnapshot(game), []);
  const snap = useGameTick(selector, tableSnapshotEq);

  // 整表前提：workshop 未建成则不渲染（left.jsx.js:700-702）
  if (!snap || !snap.workshopOn) {
    return null;
  }

  const toggleEdit = () => setIsEditMode(!isEditMode);
  const toggleCollapsed = () => setIsCollapsed(!isCollapsed);

  return (
    <div className="sidebar-section" aria-label="Craftable resources">
      <div>
        <div className="res-toolbar left">
          <a
            className="link collapse"
            onClick={toggleCollapsed}
            tabIndex={1}
            title="Toggle craft"
          >
            {isCollapsed ? ">(" + t("left.craft") + ")" : <div className="svg-icon down" />}
          </a>
        </div>
        <div className="res-toolbar right">
          <a
            className={"link" + (isEditMode ? " toggled" : "")}
            onClick={toggleEdit}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                toggleEdit();
              }
            }}
            tabIndex={1}
          >
            <div className="svg-icon gear" />
          </a>
        </div>
      </div>
      {!isCollapsed && (
        <div>
          <div className="res-table craftTable">
            {snap.names.map((name) => (
              <CraftRow
                key={name}
                name={name}
                isEditMode={isEditMode}
                isRequired={snap.reqRes.indexOf(name) >= 0}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 独立挂载入口（自带 TooltipProvider），仅供隔离测试/调试。
 * P2-20 起生产挂载由 LeftPanel 统一承担（Provider 已上提到 LeftPanel 根），
 * 本入口不再注册到 KGNewUI.panels。
 */
export default function CraftTablePanel() {
  return (
    <TooltipProvider>
      <CraftTable />
    </TooltipProvider>
  );
}
